/**
 * Timing match setup against the two budgets the PRD names.
 *
 * MS-1 allows **sixty seconds** from opening the app to the first ball, MS-2 **twenty** for
 * a second match with the same team. Both were previously asserted in prose here and in
 * `publish.ts` and measured nowhere, which makes them decoration: a screen can drift well
 * past a budget nobody counts, and the field test (#16) would have to time it by stopwatch.
 *
 * So this counts. Same shape as the sign-in friction record and for the same reason — the
 * evidence has to be readable off the device afterwards, not only in a console nobody was
 * watching.
 *
 * **The two budgets are told apart by whether a saved squad was reused**, not by whether it
 * is the scorer's first match. A rematch typed out by hand is a sixty-second job however
 * many matches came before it, and averaging the two together would hide the thing MS-2
 * exists to protect: that reusing a squad is dramatically faster than not.
 *
 * What it does not claim: this measures the *screen*, from first paint to the match
 * existing. Getting to the screen, reading a team sheet and arguing about the toss are not
 * in it, so a run inside budget here is necessary for MS-1 and not sufficient.
 */
import type { Store } from "../localStore.ts";

export const SETUP_KEY = "sportsos.setup.timing";

/** Which budget a run should be judged against (PRD MS-1 / MS-2). */
export type SetupKind = "fresh" | "reused_squad";

export interface SetupTiming {
  readonly kind: SetupKind;
  readonly seconds: number;
  /** How many players had to be typed. The dominant cost of a fresh setup. */
  readonly playersTyped: number;
  readonly budgetSeconds: number;
  readonly withinBudget: boolean;
}

const BUDGETS: Readonly<Record<SetupKind, number>> = { fresh: 60, reused_squad: 20 };

export interface SetupClock {
  /** The setup screen is up. A second call does not restart the clock. */
  started(): void;
  /**
   * The match exists on the server. Returns what to report, or null if nothing was timed —
   * which happens when storage is unavailable, and is not worth failing a match over.
   */
  finished(input: { reusedSquad: boolean; playersTyped: number }): SetupTiming | null;
}

export function setupClock(store: Store, now: () => number): SetupClock {
  const read = (): number | null => {
    const raw = store.getItem(SETUP_KEY);
    // The empty string is how a finished run is cleared, and `Number("")` is 0 — which is
    // finite, so a bare `isFinite` check would read a cleared clock as having started at
    // the epoch and report every later setup as taking fifty-six years.
    if (raw === null || raw === "") return null;
    const at = Number(raw);
    return Number.isFinite(at) && at > 0 ? at : null;
  };

  return {
    started: () => {
      // Only if nothing is running. The setup screen re-renders on every keystroke, and
      // restarting here would report every setup as instantaneous.
      if (read() !== null) return;
      try {
        store.setItem(SETUP_KEY, String(now()));
      } catch {
        // Private browsing throws. Losing the measurement is acceptable.
      }
    },

    finished: ({ reusedSquad, playersTyped }) => {
      const startedAt = read();
      if (startedAt === null) return null;
      try {
        store.setItem(SETUP_KEY, "");
      } catch {
        // Nothing to do; the next `started()` overwrites it anyway.
      }
      const kind: SetupKind = reusedSquad ? "reused_squad" : "fresh";
      const seconds = Math.round((now() - startedAt) / 1000);
      const budgetSeconds = BUDGETS[kind];
      return {
        kind,
        seconds,
        playersTyped,
        budgetSeconds,
        withinBudget: seconds <= budgetSeconds,
      };
    },
  };
}
